import {
  DepressionContentPage,
  FAQItem,
} from "@/components/DepressionContentPage";
import { Link } from "@/lib/router-compat";
import { RelatedLink } from "@/components/RelatedPages";
import { SaintComparisonStatBand } from "@/components/SaintComparisonInline";

const faqs: FAQItem[] = [
  {
    question: "How can I tell if my teenager is depressed or just moody?",
    answer:
      "The single most useful distinction is duration and interference. A teenager who is withdrawn and irritable for a few days after a breakup or a bad grade is reacting to something. A teenager whose low mood, withdrawal, or irritability has persisted for two weeks or more, and who no longer seems lifted by the things that used to work — friends, hobbies, family time — may have a depressive illness. The tell isn't that they're sad. It's that the things that should help don't.",
  },
  {
    question: "What does depression look like in a teenager?",
    answer:
      "Teen depression frequently shows up as something other than sadness first: irritability, defiance, or rebellious behavior that seems new; retreating to their room for hours; sleeping all day and awake all night; a sudden drop in grades; changes in eating; physical complaints with no clear cause; trouble concentrating; extreme sensitivity to rejection; loss of interest in friends or activities; alcohol or drug use; or talk of hopelessness. If several of these are present and represent a change from who your kid used to be, take it seriously.",
  },
  {
    question: "Is SAINT cleared for teenagers?",
    answer:
      "No. SAINT is FDA-cleared for adults with major depressive disorder who haven't gotten adequate relief from antidepressants in their current episode. It is not cleared for anyone under 18. However, if your teen is 18 or older and has already been through therapy and one or more antidepressants without adequate relief, they are a legitimate candidate. Some standard TMS systems have received FDA clearance for adolescents aged 15 and older as an add-on treatment.",
  },
  {
    question: "Does my own depression affect my teenager's risk?",
    answer:
      "Yes. Depression runs in families and tends to first appear between ages 15 and 30. If you, your partner, or a close relative has depression or anxiety, your teenager's risk is higher, and their first episode is likely to come earlier. Research following families over time shows that when a depressed parent reaches remission, their children's symptoms often improve, even without direct treatment of the child. Treating yourself effectively is one of the most protective things you can do.",
  },
  {
    question: "What should I do if I think my teen is depressed?",
    answer:
      "If the signs have lasted two weeks or more, make an appointment with their pediatrician or a mental health professional this week. Treatment typically involves psychotherapy, often cognitive behavioral therapy, and in moderate to severe cases, medication with close monitoring. If your teen is 18 or older and has already tried medication without relief, or if you recognized yourself in this article, call us. If you see warning signs of suicide — talk of hopelessness, giving away possessions, goodbye messages, threats of self-harm — don't wait for an appointment. Call or text 988 or go to the emergency room.",
  },
  {
    question: "Do I have to stop my medications to do SAINT as a parent?",
    answer:
      "No. Most patients continue their existing medication regimen through SAINT treatment. SAINT is not a replacement for what you've built — it is a different kind of intervention aimed at the brain circuitry your medications may not have reached. Never change medication without guidance from your prescribing provider.",
  },
];

const related: RelatedLink[] = [
  {
    title: "Is My Child Depressed? What Parents Should Know",
    href: "/is-my-child-depressed-what-parents-should-know",
    description:
      "Signs of childhood depression, when to seek help, and why your own depression matters.",
  },
  {
    title:
      '"It Runs in My Family." What That Actually Means for Your Depression',
    href: "/it-runs-in-my-family-what-that-means-for-your-depression",
    description:
      "How family history shapes depression risk — and why it doesn't decide whether treatment works.",
  },
  {
    title:
      "You Just Started Therapy at 27. That's Not Late. That's the Beginning.",
    href: "/you-just-started-therapy-at-27-thats-not-late",
    description:
      "What the first weeks of treatment feel like and how to be your own advocate early.",
  },
  {
    title: "What Is SAINT TMS Therapy?",
    href: "/learn/what-is-saint-tms",
    description: "How the five-day, fMRI-guided protocol works.",
  },
];

export default function TeenDepression() {
  return (
    <DepressionContentPage
      seo={{
        title: "Teen Depression: Hard Year or Real Illness? | Neuro Wellness",
        description:
          "How parents can tell the difference between teenage moodiness and clinical depression, warning signs, family history, and treatment options including SAINT for young adults 18+.",
        canonical: "/teen-depression-hard-year-or-real-illness",
        keywords:
          "teen depression, teenage depression signs, depressed teenager, adolescent depression, parent guide teen depression, SAINT TMS young adults, treatment-resistant depression, family history depression, teen mental health, when to seek help teenager",
      }}
      badge="For Families"
      heroTitle="Teen Depression: How to Tell the Difference Between a Hard Year and a Real Illness"
      heroSubtitle="Teenagers are moody — that's a developmental stage, not a diagnosis. But when the bad days don't end and the things that should help don't, it may be something more. Here's how to tell the difference, and what to do about it."
      lastUpdated="2026-11-03"
      sections={[
        {
          body: (
            <div className="space-y-5">
              <p>
                Teenagers are moody. That's not a diagnosis; it's a
                developmental stage. Hormones, social pressure, school stress,
                identity questions, and a brain that's still under construction
                all combine to produce mood swings that would alarm anyone if
                they showed up in an adult.
              </p>
              <p>
                So how does a parent tell the difference between a teenager
                having a rough stretch and a teenager who is clinically
                depressed? It's a real question, and it matters, because roughly
                one in eight adolescents experiences depression, and most of
                them don't get treated for it until years later, if at all.
              </p>
              <p>
                This article is written for the parent lying awake wondering
                which one they're looking at.
              </p>
            </div>
          ),
        },
        {
          heading: "The two-week test",
          body: (
            <div className="space-y-5">
              <p>
                The single most useful distinction is duration and interference.
                A teenager who is withdrawn and irritable for a few days after a
                breakup or a bad grade is reacting to something. A teenager
                whose low mood, withdrawal, or irritability has persisted for
                two weeks or more, and who no longer seems lifted by the things
                that used to work (friends, hobbies, family time), may have a
                depressive illness.
              </p>
              <p>
                The tell isn't that they're sad. It's that the things that
                should help don't.
              </p>
            </div>
          ),
        },
        {
          heading: "What it looks like in a teenager",
          body: (
            <div className="space-y-5">
              <p>
                Adult depression is often visible as sadness. Teen depression
                frequently shows up as something else first:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  Irritability, defiance, or rebellious behavior that seems new
                </li>
                <li>Retreating to their room for hours, door closed</li>
                <li>
                  Sleeping all day and awake all night, or unable to sleep at
                  all
                </li>
                <li>
                  A sudden drop in grades or a pattern of skipping class and
                  missing obligations
                </li>
                <li>
                  Changes in eating that lead to noticeable weight loss or gain
                </li>
                <li>
                  Physical complaints (headaches, stomachaches, back pain,
                  fatigue) with no clear cause
                </li>
                <li>
                  Trouble concentrating, remembering, or making simple decisions
                </li>
                <li>Extreme sensitivity to rejection or failure</li>
                <li>Unexplained crying</li>
                <li>
                  Loss of interest in friends or activities they used to care
                  about
                </li>
                <li>
                  Alcohol or drug use, risky sexual behavior, or reckless
                  choices
                </li>
                <li>Talk of hopelessness, or any preoccupation with death</li>
              </ul>
              <p>
                Not every depressed teen shows all of these. But if several are
                present and represent a change from who your kid used to be,
                take it seriously.
              </p>
            </div>
          ),
        },
        {
          heading: "The family history piece",
          body: (
            <div className="space-y-5">
              <p>
                Depression runs in families, and it tends to first appear
                between ages 15 and 30. If you, your partner, or a close
                relative has depression or anxiety, your teenager's risk is
                higher, and their first episode is likely to come earlier.
              </p>
              <p>
                That's not a reason for guilt. It's a reason to lower your
                threshold for concern and to be honest with your teen that this
                is something the family deals with, and something that can be
                treated.
              </p>
            </div>
          ),
        },
        {
          heading: "How it's diagnosed and treated",
          body: (
            <div className="space-y-5">
              <p>
                There's no blood test for depression. A mental health
                professional will interview your teen, and usually you, and may
                gather input from teachers. They'll also screen for conditions
                that often travel with depression, particularly anxiety and
                substance use, and for more complex forms like bipolar disorder.
              </p>
              <p>
                Treatment typically involves psychotherapy, often cognitive
                behavioral therapy, and in moderate to severe cases, medication.
                A large NIMH-funded study found that the combination of
                medication and CBT was substantially more effective than either
                alone, with nearly three in four teens improving.
                Antidepressants in adolescents require close monitoring because
                of an FDA warning about suicidal thinking, but they are
                effective and widely used.
              </p>
              <p>
                For teens who don't respond adequately to those steps, some
                standard TMS systems have received FDA clearance for adolescents
                aged 15 and older as an add-on treatment. That's worth raising
                with your teen's psychiatrist.
              </p>
            </div>
          ),
        },
        {
          heading: "Where SAINT fits, honestly",
          body: (
            <div className="space-y-4">
              <p>
                SAINT (Stanford Accelerated Intelligent Neuromodulation Therapy)
                is an accelerated, FDA-cleared form of transcranial magnetic
                stimulation, developed at Stanford's Brain Stimulation Lab. It's
                cleared for adults with major depressive disorder who haven't
                gotten adequate relief from antidepressants in their current
                episode. It is not cleared for anyone under 18.
              </p>
              <p>That matters for two groups of readers.</p>
              <p>
                <strong>If your teen is 18 or older.</strong> An 18- or
                19-year-old who has already been through therapy and one or more
                antidepressants without adequate relief is a legitimate
                candidate for SAINT. The 18-to-24 age group carries one of the
                highest risks for completed suicide, and a treatment that can
                produce remission in days rather than months is worth knowing
                about. In the clinical trials behind SAINT's clearance, 79
                percent of participants reached remission, with an average time
                to remission of 2.6 days.
              </p>
              <p>
                <strong>If you're the parent, and you're depressed too.</strong>{" "}
                Research following families over time shows that when a
                depressed parent reaches remission, their children's symptoms
                improve, even without direct treatment of the child. If you've
                been managing your own depression on medications that only
                partly work while trying to hold your teenager together,
                treating yourself effectively is not selfish. It's one of the
                most protective things you can do.
              </p>

              <SaintComparisonStatBand />

              <p>
                Here's how SAINT works at a{" "}
                <Link
                  to="/find-a-verified-saint-provider"
                  className="font-semibold text-primary hover:underline"
                >
                  Verified SAINT Provider
                </Link>{" "}
                like Neuro Wellness TMS Centers of America:
              </p>
              <ol className="list-decimal pl-5 space-y-3">
                <li>
                  <strong>Consultation.</strong> Our clinical team reviews the
                  patient's history and medication trials and confirms
                  candidacy. For a young adult, a parent is welcome in the room
                  if the patient wants them there. The initial consultation is a
                  Zoom call; as steps evolve, you'll also have the option to
                  meet with our psychiatrist by Zoom web conference or in
                  person. The mandatory in-person steps include fMRI mapping and
                  the actual treatment.
                </li>
                <li>
                  <strong>fMRI brain scan.</strong> A roughly 45-minute
                  functional MRI maps how the individual brain's networks are
                  wired.
                </li>
                <li>
                  <strong>Personalized targeting.</strong> A targeting algorithm
                  reads the scan and identifies the exact location for
                  stimulation. Neuronavigation guides the coil there for every
                  session.
                </li>
                <li>
                  <strong>Five-day stimulation series.</strong> Ten 10-minute
                  sessions per day, with 50-minute breaks in between, for five
                  consecutive days. Five clinic visits, compared with 30 to 36
                  for conventional TMS. For a college student, that fits inside
                  a single break.
                </li>
              </ol>
              <p>
                Across more than 400 trial patients, 85 percent experienced
                symptom relief, with no serious adverse events. Side effects
                were generally mild: headache, fatigue, and temporary scalp
                discomfort. SAINT isn't appropriate for people with
                non-removable metal in or near the head or an implanted device.
                Current medications don't need to be stopped.
              </p>
            </div>
          ),
        },
        {
          heading: "What you can do at home, starting now",
          body: (
            <div className="space-y-4">
              <p>
                Whatever treatment path you take, a few things consistently
                help:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  Replace shame and punishment with recognition of what they're
                  doing right
                </li>
                <li>Let them make mistakes without rescuing or lecturing</li>
                <li>
                  Listen more than you advise, even when the problem seems small
                  to you
                </li>
                <li>
                  Keep the door open when they push you away; the pushing is the
                  illness, not the kid
                </li>
                <li>
                  If there's a trusted adult they'll talk to, encourage that
                </li>
              </ul>
              <p>
                And if you see warning signs of suicide (talk of hopelessness,
                giving away possessions, goodbye messages, sudden substance use,
                threats of self-harm), don't wait for an appointment. Call 988
                or go to the emergency room.
              </p>
            </div>
          ),
        },
        {
          heading: "What to do next",
          body: (
            <div className="space-y-4">
              <p>
                If you recognized your teenager in this article, make an
                appointment with their pediatrician or a mental health
                professional this week. If your teen is 18 or older and has
                already tried medication without relief, or if you recognized
                yourself, call us.
              </p>
              <p>
                At Neuro Wellness TMS Centers of America, the first appointment
                is an evaluation, not a commitment. We offer private outpatient
                care with a dedicated care team and weekend availability. We'll
                tell you honestly whether SAINT is a fit. Insurance coverage
                varies by plan; our team can help you understand your options,
                including prior authorization and self-pay.
              </p>
              <p className="font-semibold text-primary">
                Schedule a consultation:{" "}
                <a
                  href="tel:9548279793"
                  className="text-primary hover:underline"
                >
                  954-827-9793
                </a>
              </p>
              <p className="text-xs text-muted-foreground italic pt-2">
                If your teen talks about suicide, gives away possessions, sends
                goodbye messages, or threatens self-harm, treat it as urgent.
                Call or text 988 (Suicide and Crisis Lifeline) or go to your
                nearest emergency room. This article is for educational purposes
                and is not a substitute for individual medical advice.
              </p>
            </div>
          ),
        },
      ]}
      faq={faqs}
      relatedLinks={related}
      finalCtaText="Schedule a Consultation"
    />
  );
}
